import { useEffect } from 'react';
import { useSearchParams } from 'react-router';

import Dropdown from '@cartwhl/ui/atoms/Dropdown';
import Text from '@cartwhl/ui/base/TextNew';
import SearchIcon from '@cartwhl/ui/icons/SearchIcon';

import { useStore } from '../dashboardStore';
import {
  type DashboardHomeTab,
  type DashboardWorkTypeFilter,
  isDashboardHomeTab,
  isDashboardWorkFilter,
} from '../types';
import DashboardGettingStarted from './DashboardGettingStarted';
import DashboardWorkList from './DashboardWorkList';

const HOME_TABS: Array<{ label: string; value: DashboardHomeTab }> = [
  { label: 'My work', value: 'mine' },
  { label: 'Team work', value: 'team' },
  { label: 'Get started!', value: 'getting-started' },
];

const WORK_TYPE_FILTERS: Array<{
  label: string;
  value: DashboardWorkTypeFilter;
}> = [
  { label: 'All types', value: 'all' },
  { label: 'Cartwheel Scenes', value: 'scene' },
  { label: 'Canvas Projects', value: 'canvas-project' },
  { label: 'Performance Capture Jobs', value: 'comic-job' },
  { label: 'Performance Generation Jobs', value: 'swing-job' },
  { label: 'Character Jobs', value: 'character-job' },
];

export default function DashboardHomeTabs() {
  const dashboardStore = useStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const { homeTab, workItemsSearchQuery, workItemsTypeFilter } =
    dashboardStore.state;
  const showWorkControls = isDashboardWorkFilter(homeTab);

  const setHomeTab = (tab: DashboardHomeTab) => {
    dashboardStore.setHomeTab(tab);
    const nextParams = new URLSearchParams(searchParams);
    if (tab === 'mine') {
      nextParams.delete('tab');
    } else {
      nextParams.set('tab', tab);
    }
    setSearchParams(nextParams, { replace: true });
  };

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (isDashboardHomeTab(tab) && tab !== dashboardStore.state.homeTab) {
      dashboardStore.setHomeTab(tab);
    }
  }, [dashboardStore, searchParams]);

  return (
    <div className="mx-auto flex w-full max-w-[1020px] flex-col gap-8">
      <DashboardHomeTabsHeader
        activeTab={homeTab}
        activeTypeFilter={workItemsTypeFilter}
        searchQuery={workItemsSearchQuery}
        showWorkControls={showWorkControls}
        onTabChange={setHomeTab}
        onSearchQueryChange={(query) =>
          dashboardStore.setWorkItemsSearchQuery(query)
        }
        onTypeFilterChange={(filter) =>
          dashboardStore.setWorkItemsTypeFilter(filter)
        }
      />
      {showWorkControls ? (
        <DashboardWorkList
          workFilter={homeTab}
          onGoToGettingStarted={() => setHomeTab('getting-started')}
        />
      ) : (
        <DashboardGettingStarted />
      )}
    </div>
  );
}

interface DashboardHomeTabsHeaderProps {
  activeTab: DashboardHomeTab;
  activeTypeFilter: DashboardWorkTypeFilter;
  searchQuery: string;
  showWorkControls: boolean;
  onTabChange: (tab: DashboardHomeTab) => void;
  onSearchQueryChange: (query: string) => void;
  onTypeFilterChange: (filter: DashboardWorkTypeFilter) => void;
}

function DashboardHomeTabsHeader({
  activeTab,
  activeTypeFilter,
  searchQuery,
  showWorkControls,
  onTabChange,
  onSearchQueryChange,
  onTypeFilterChange,
}: DashboardHomeTabsHeaderProps) {
  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-0.5 sm:gap-2">
        {HOME_TABS.map((tab) => {
          const isActive = tab.value === activeTab;

          return (
            <button
              key={tab.value}
              type="button"
              aria-pressed={isActive}
              className={`flex min-w-0 flex-1 items-center justify-center rounded-lg px-2 py-3 transition-colors sm:flex-none sm:px-4 ${
                isActive ? 'bg-secondary' : 'hover:bg-secondary'
              }`}
              onClick={() => onTabChange(tab.value)}
            >
              <Text token="14/medium" className="text-primary text-center">
                {tab.label}
              </Text>
            </button>
          );
        })}
      </div>

      {showWorkControls ? (
        <div className="flex w-full items-center gap-4 sm:w-auto">
          <label className="bg-secondary flex h-10 min-w-0 flex-1 items-center gap-2 rounded-lg py-2.5 pl-4 pr-2 sm:w-[270px] sm:flex-none">
            <span className="sr-only">Search recent work titles</span>
            <Text
              as="input"
              token="14/medium"
              type="search"
              value={searchQuery}
              placeholder="Search work"
              className="placeholder:text-tertiary text-primary min-w-0 flex-1 bg-transparent outline-none"
              onChange={(event) =>
                onSearchQueryChange(event.currentTarget.value)
              }
            />
            <SearchIcon className="text-primary size-5 flex-none" />
          </label>

          <Dropdown
            align="end"
            width={320}
            trigger={
              <button
                type="button"
                aria-label={`Filter work type: ${getWorkTypeFilterLabel(activeTypeFilter)}`}
                className={`border-primary text-primary hover:bg-secondary flex h-8 w-8 flex-none items-center justify-center rounded-lg border p-[5px] transition-colors ${
                  activeTypeFilter === 'all' ? '' : 'bg-secondary'
                }`}
              >
                <FilterIcon className="size-5" />
              </button>
            }
          >
            {WORK_TYPE_FILTERS.map((filter) => {
              const isActive = filter.value === activeTypeFilter;

              return (
                <Dropdown.Item
                  key={filter.value}
                  icon={isActive ? <SelectedFilterIcon /> : null}
                  onClick={() => onTypeFilterChange(filter.value)}
                >
                  {filter.label}
                </Dropdown.Item>
              );
            })}
          </Dropdown>
        </div>
      ) : null}
    </div>
  );
}

function getWorkTypeFilterLabel(filter: DashboardWorkTypeFilter) {
  return (
    WORK_TYPE_FILTERS.find((option) => option.value === filter)?.label ??
    'All types'
  );
}

function FilterIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      className={className}
      aria-hidden="true"
    >
      <path
        d="M4 5.75H16M6.5 10H13.5M8.5 14.25H11.5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

function SelectedFilterIcon() {
  return (
    <svg
      viewBox="0 0 6 6"
      fill="currentColor"
      className="text-primary h-1.5 w-1.5"
      aria-hidden="true"
    >
      <circle cx="3" cy="3" r="3" />
    </svg>
  );
}
